import { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  Sunrise, Flame, AlertTriangle, TrendingUp, Sparkles, RefreshCw,
  ArrowRight, CheckCircle2, XCircle, Clock, Wallet, Zap, Award,
  Target, PhoneCall, Handshake, GraduationCap, ShieldAlert, MapPin,
  Users, ChevronRight, ChevronDown, ChevronUp, HelpCircle, Beaker,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  PageShell, PageHeader, Card, Section, Pill, PrimaryButton, GhostButton,
  Metric, Loader, EmptyState, Alert,
  NAVY, CHAMPAGNE, GOLD, IVORY, SLATE, GREEN_LEAF, RED_LEAF,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { altioraToast } from "@/components/altiora/toast";
import TrajectoryRing from "@/components/altiora/TrajectoryRing";
import TrajectoryTimeline from "@/components/altiora/TrajectoryTimeline";
import { ReasoningTrigger } from "@/components/altiora/ReasoningWidget";
import QuickOutcomeButtons from "@/components/altiora/QuickOutcomeButtons";
import DimensionDetailModal from "@/components/altiora/DimensionDetailModal";
import ActionSimulatorModal from "@/components/altiora/ActionSimulatorModal";

/**
 * MorningBriefing — le "Morning Command Center" du conseiller.
 *
 * Cette page devient l'écran d'accueil par défaut. Elle répond chaque matin
 * à la question « Que dois-je faire aujourd'hui ? » en agrégant tous les
 * signaux (Sales OS, Revenus, ODE, Digital Twin, Gamification, LMS).
 *
 * Livraison progressive (MVP) — 5 sections :
 *   1. Actions prioritaires du jour
 *   2. Décisions recommandées
 *   3. Opportunités détectées
 *   4. Risques immédiats
 *   5. Rappels & relances
 */
export default function MorningBriefing() {
  const [data, setData] = useState(null);
  const [trajectory, setTrajectory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [markedIds, setMarkedIds] = useState(new Set());
  const [selectedDim, setSelectedDim] = useState(null);
  const [simActionId, setSimActionId] = useState(null);

  const load = useCallback(async (force = false) => {
    try {
      if (force) setRefreshing(true); else setLoading(true);
      const endpoint = force
        ? "/strategic-copilot/briefing/refresh"
        : "/strategic-copilot/briefing/today";
      const res = force
        ? await api.post(endpoint)
        : await api.get(endpoint);
      setData(res.data);
      setMarkedIds(new Set([
        ...(res.data.completed_action_ids || []),
        ...(res.data.dismissed_action_ids || []),
      ]));
      // Load trajectory (parallel fetch)
      try {
        const t = await api.get("/trajectory/current");
        setTrajectory(t.data);
      } catch (_) { /* trajectory is optional */ }
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Chargement impossible.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const handleMark = useCallback(async (actionId, outcome) => {
    try {
      await api.post(`/strategic-copilot/actions/${actionId}/${outcome}`);
      setMarkedIds((prev) => new Set([...prev, actionId]));
      altioraToast.success(
        outcome === "done" ? "Bravo — action marquée comme faite." : "Action écartée."
      );
    } catch (e) {
      altioraToast.error("Impossible d'enregistrer votre feedback.");
    }
  }, []);

  if (loading) {
    return (
      <PageShell testid="briefing-loading">
        <TopBar />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-8 pb-20">
          <Loader label="Composition de votre briefing du jour…" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell testid="morning-briefing-root">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-8 md:pt-12 pb-20">
        {error && (
          <Alert variant="danger" title="Briefing indisponible" testid="briefing-error">
            {error}
          </Alert>
        )}
        {data && (
          <>
            <Hero
              summary={data.summary}
              directorSpeech={data.director_speech}
              narrative={data.ai_narrative}
              generatedBy={data.generated_by}
              onRefresh={() => load(true)}
              refreshing={refreshing}
            />
            {trajectory && (
              <div className="mt-8">
                <TrajectoryRing
                  trajectory={trajectory}
                  onDimensionClick={(d) => setSelectedDim(d)}
                />
              </div>
            )}
            <TopMetrics summary={data.summary} className="mt-6" />
            <div className="mt-8">
              <TrajectoryTimeline />
            </div>
            <div className="mt-10 space-y-10">
              {(data.sections || []).map((section) => (
                <BriefingSectionView
                  key={section.key}
                  section={section}
                  markedIds={markedIds}
                  onMark={handleMark}
                  onSimulate={(a) => setSimActionId(a)}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <DimensionDetailModal
        dim={selectedDim}
        open={!!selectedDim}
        onClose={() => setSelectedDim(null)}
      />
      <ActionSimulatorModal
        action={simActionId}
        open={!!simActionId}
        onClose={() => setSimActionId(null)}
      />
    </PageShell>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Hero — greeting + director speech + refresh (conversational)
// ═══════════════════════════════════════════════════════════════════
function Hero({ summary, directorSpeech, narrative, generatedBy, onRefresh, refreshing }) {
  const aiEnabled = generatedBy?.includes("ai");
  const speech = directorSpeech || narrative;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
      data-testid="briefing-hero"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className="text-[10px] font-mono uppercase tracking-[0.28em] px-2 py-1 rounded"
          style={{ border: `1px solid ${CHAMPAGNE}`, color: CHAMPAGNE }}
          data-testid="briefing-date-badge"
        >
          {summary.weekday_fr} · {summary.date}
        </span>
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.32em]"
          style={{ color: CHAMPAGNE }}
        >
          ALTIORA · Directeur de développement commercial
        </span>
        <span
          className="ml-auto text-[10px] font-mono px-2 py-1 rounded"
          style={{
            border: `1px solid ${aiEnabled ? GOLD : SLATE}55`,
            color: aiEnabled ? GOLD : SLATE,
          }}
          data-testid="briefing-engine-badge"
        >
          Confiance jumeau : {summary.trust_index}/100
        </span>
      </div>

      {/* Director speech — conversational monologue */}
      <div
        className="mt-6 rounded-2xl p-6 md:p-8"
        style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #061528 100%)`,
          boxShadow: "0 8px 32px -12px rgba(11,37,69,0.35)",
        }}
        data-testid="director-speech-panel"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0"
               style={{ background: `${GOLD}22`, color: GOLD }}>
            <Sunrise className="h-4 w-4" strokeWidth={1.8} />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.32em]"
               style={{ color: GOLD, paddingTop: 12 }}>
            Briefing du {summary.weekday_fr}
          </div>
        </div>
        <p
          className="font-serif text-[19px] md:text-[22px] leading-relaxed"
          style={{ color: IVORY, whiteSpace: "pre-line" }}
          data-testid="director-speech-body"
        >
          {speech}
        </p>
      </div>

      {/* Headline (kept smaller now that director speech does the work) */}
      <div className="mt-6 max-w-3xl">
        <h1
          className="font-serif tracking-tight leading-[1.1] font-medium"
          style={{ color: NAVY, fontSize: "clamp(1.5rem, 3vw, 2.2rem)" }}
          data-testid="briefing-greeting"
        >
          {summary.greeting}{" "}
          <span style={{ color: GOLD, fontStyle: "italic" }}>
            {summary.headline}
          </span>
        </h1>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <GhostButton onClick={onRefresh} disabled={refreshing} testid="briefing-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 inline ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Régénération…" : "Régénérer le briefing"}
        </GhostButton>
        <Link to="/app/twin" data-testid="briefing-cta-twin">
          <PrimaryButton>
            Ouvrir mon jumeau numérique <ArrowRight className="h-3.5 w-3.5 ml-1.5 inline" />
          </PrimaryButton>
        </Link>
      </div>
    </motion.div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Top metrics
// ═══════════════════════════════════════════════════════════════════
function TopMetrics({ summary, className = "" }) {
  const fmt = (v) => (Number(v || 0)).toLocaleString("fr-CA");
  return (
    <div
      className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${className}`}
      data-testid="briefing-metrics"
    >
      <Metric
        testid="metric-actions"
        label="Actions du jour"
        value={summary.total_actions}
        hint={
          summary.critical_actions > 0
            ? `${summary.critical_actions} critique(s)`
            : "Tout sous contrôle"
        }
        accent={summary.critical_actions > 0 ? "#B45309" : NAVY}
      />
      <Metric
        testid="metric-gain"
        label="Gain potentiel"
        value={`${fmt(summary.potential_gain_cad)} $`}
        hint={`≈ ${summary.estimated_total_minutes} min · CAD`}
        accent={GOLD}
      />
      <Metric
        testid="metric-pipeline"
        label="Pipeline ouvert"
        value={summary.pipeline_open_count}
        hint={`${fmt(summary.pipeline_open_value_cad)} $ estimés`}
        accent="#065F46"
      />
      <Metric
        testid="metric-streak"
        label={`Série · ${summary.streak_days} j`}
        value={summary.xp_total}
        hint={summary.revenue_goal_cad > 0
          ? `Objectif annuel : ${summary.revenue_progress_pct}%`
          : "XP totale"}
        accent="#5B21B6"
      />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Section view
// ═══════════════════════════════════════════════════════════════════
const SECTION_ICON = {
  today_actions:   Flame,
  decisions:       Target,
  opportunities:   Sparkles,
  immediate_risks: AlertTriangle,
  reminders:       Clock,
};

function BriefingSectionView({ section, markedIds, onMark, onSimulate }) {
  const Icon = SECTION_ICON[section.key] || Sparkles;
  const visible = section.items.filter((a) => !markedIds.has(a.id));
  if (!visible.length) {
    return (
      <Section
        icon={Icon}
        title={section.title}
        hint={section.subtitle}
        testid={`section-${section.key}`}
      >
        <EmptyState
          icon={CheckCircle2}
          title="Rien à traiter ici."
          description="Cette section n'a aucune action prioritaire aujourd'hui."
          testid={`section-empty-${section.key}`}
        />
      </Section>
    );
  }
  return (
    <div data-testid={`section-${section.key}`}>
      <div className="flex items-baseline gap-3 mb-4">
        <Icon className="h-4 w-4" style={{ color: CHAMPAGNE }} strokeWidth={1.8} />
        <h2 className="font-serif text-xl md:text-2xl tracking-tight" style={{ color: NAVY }}>
          {section.title}
        </h2>
        <span className="text-[10px] font-mono uppercase tracking-[0.28em]" style={{ color: SLATE }}>
          {visible.length} action{visible.length > 1 ? "s" : ""}
        </span>
      </div>
      <p className="text-[13px] mb-4 max-w-3xl" style={{ color: SLATE }}>
        {section.subtitle}
      </p>
      <AnimatePresence initial={false}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visible.map((action, idx) => (
            <ActionCard
              key={action.id}
              action={action}
              rank={idx + 1}
              onDone={() => onMark(action.id, "done")}
              onDismiss={() => onMark(action.id, "dismiss")}
              onSimulate={() => onSimulate?.(action)}
            />
          ))}
        </div>
      </AnimatePresence>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Action card — the atomic unit of a briefing
// ═══════════════════════════════════════════════════════════════════
const PRIORITY_META = {
  critical: { label: "Critique",  color: "#B91C1C", tone: "red"   },
  high:     { label: "Haute",     color: "#B45309", tone: "amber" },
  medium:   { label: "Moyenne",   color: "#0B2545", tone: "navy"  },
  low:      { label: "Faible",    color: SLATE,     tone: "neutral" },
};

const CATEGORY_ICON = {
  renewal:      RefreshCw,
  hot_prospect: Flame,
  call_back:    PhoneCall,
  opportunity:  Sparkles,
  partner:      Handshake,
  networking:   Users,
  risk:         ShieldAlert,
  admin:        Award,
  training:     GraduationCap,
  self_care:    Zap,
};

function ActionCard({ action, rank, onDone, onDismiss, onSimulate }) {
  const navigate = useNavigate();
  const [whyOpen, setWhyOpen] = useState(false);
  const meta = PRIORITY_META[action.priority] || PRIORITY_META.medium;
  const Icon = CATEGORY_ICON[action.category] || Target;
  const openCta = () => {
    if (action.cta_link) navigate(action.cta_link);
  };
  const hasWhyDetails = action.why_today || action.why_more_important
                        || (action.data_used?.length > 0)
                        || (action.dimension_impacts?.length > 0);

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 20 }}
      whileHover={{ y: -3 }}
      transition={{ duration: 0.25, ease: [0.2, 0.7, 0.2, 1] }}
      className="group h-full rounded-xl bg-white p-5 flex flex-col gap-3"
      style={{
        border: `1px solid ${CHAMPAGNE}33`,
        boxShadow: "0 2px 8px -3px rgba(11,37,69,0.06)",
      }}
      data-testid={`action-card-${action.id}`}
    >
      {/* Header row */}
      <div className="flex items-start gap-3">
        <div
          className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
        >
          <Icon className="h-5 w-5" strokeWidth={1.7} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Pill tone={meta.tone} testid={`action-priority-${action.id}`}>
              {meta.label}
            </Pill>
            <span
              className="text-[10px] font-mono uppercase tracking-[0.22em]"
              style={{ color: SLATE }}
            >
              #{rank} · {action.priority_score}/100
            </span>
            {action.confidence_score > 0 && (
              <span
                className="text-[10px] font-mono uppercase tracking-[0.22em] px-1.5 py-0.5 rounded"
                style={{ background: `${GOLD}22`, color: "#7A5B14" }}
                data-testid={`action-confidence-${action.id}`}
                title="Niveau de confiance de la recommandation"
              >
                Confiance {action.confidence_score}
              </span>
            )}
          </div>
          <h3
            className="mt-1.5 font-serif text-lg leading-tight tracking-tight"
            style={{ color: NAVY }}
            data-testid={`action-title-${action.id}`}
          >
            {action.title}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <ReasoningTrigger
              actionId={action.id}
              preloaded={{
                trace: action.reasoning_trace,
                citations: action.knowledge_citations,
                title: action.title,
              }}
            />
            <QuickOutcomeButtons actionId={action.id} />
          </div>
        </div>
      </div>

      {/* Why */}
      <p
        className="text-[13px] leading-relaxed"
        style={{ color: NAVY }}
        data-testid={`action-why-${action.id}`}
      >
        <span
          className="text-[10px] font-mono uppercase tracking-[0.22em] mr-1.5"
          style={{ color: CHAMPAGNE }}
        >
          Pourquoi
        </span>
        {action.why}
      </p>

      {/* Metadata grid */}
      <div className="grid grid-cols-2 gap-3 mt-1">
        <MetaBlock
          icon={Wallet}
          label="Gain attendu"
          value={action.expected_gain || "—"}
          accent={GREEN_LEAF}
          testid={`action-gain-${action.id}`}
        />
        <MetaBlock
          icon={Clock}
          label="Temps estimé"
          value={`${action.estimated_minutes} min`}
          accent={CHAMPAGNE}
          testid={`action-time-${action.id}`}
        />
      </div>

      {(action.resources_needed?.length > 0 || action.contact_name) && (
        <div className="flex items-start gap-2 flex-wrap text-[12px]" style={{ color: SLATE }}>
          <span
            className="text-[10px] font-mono uppercase tracking-[0.22em]"
            style={{ color: CHAMPAGNE }}
          >
            Ressources
          </span>
          {action.contact_name && (
            <span
              className="px-2 py-0.5 rounded"
              style={{ background: `${NAVY}0d`, color: NAVY }}
              data-testid={`action-contact-${action.id}`}
            >
              {action.contact_name}
            </span>
          )}
          {(action.resources_needed || []).map((r, i) => (
            <span
              key={i}
              className="px-2 py-0.5 rounded"
              style={{ background: `${CHAMPAGNE}22`, color: NAVY }}
            >
              {r}
            </span>
          ))}
        </div>
      )}

      {/* Risk if ignored */}
      {action.risk_if_ignored && (
        <div
          className="text-[12px] leading-relaxed rounded-md p-2.5 border-l-2"
          style={{
            background: `${RED_LEAF}18`,
            borderColor: "#B91C1C",
            color: "#7F1D1D",
          }}
          data-testid={`action-risk-${action.id}`}
        >
          <span className="font-semibold">Si vous ne le faites pas : </span>
          {action.risk_if_ignored}
        </div>
      )}

      {/* Expandable: "Pourquoi je te recommande cela aujourd'hui" */}
      {hasWhyDetails && (
        <div className="border-t pt-2" style={{ borderColor: `${CHAMPAGNE}22` }}>
          <button
            onClick={() => setWhyOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.24em] hover:opacity-70 transition-opacity"
            style={{ color: CHAMPAGNE }}
            data-testid={`action-why-toggle-${action.id}`}
          >
            <HelpCircle className="h-3 w-3" strokeWidth={2} />
            Pourquoi je te recommande cela aujourd'hui
            {whyOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
          <AnimatePresence initial={false}>
            {whyOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="mt-3 space-y-2.5 text-[12.5px]"
                style={{ color: NAVY }}
                data-testid={`action-why-details-${action.id}`}
              >
                {action.why_today && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-0.5"
                         style={{ color: SLATE }}>
                      Pourquoi aujourd'hui
                    </div>
                    <p className="leading-relaxed">{action.why_today}</p>
                  </div>
                )}
                {action.why_more_important && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-0.5"
                         style={{ color: SLATE }}>
                      Pourquoi plus important que les autres
                    </div>
                    <p className="leading-relaxed">{action.why_more_important}</p>
                  </div>
                )}
                {action.data_used?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-1"
                         style={{ color: SLATE }}>
                      Données utilisées
                    </div>
                    <ul className="space-y-0.5">
                      {action.data_used.map((d, i) => (
                        <li key={i} className="pl-2">· {d}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {action.dimension_impacts?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-1"
                         style={{ color: SLATE }}>
                      Dimensions impactées
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {action.dimension_impacts.map((k, i) => (
                        <span key={i}
                              className="text-[10px] font-mono uppercase tracking-[0.20em] px-1.5 py-0.5 rounded"
                              style={{ background: `${NAVY}0f`, color: NAVY }}>
                          {k}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* CTAs */}
      <div className="mt-auto pt-2 flex items-center gap-2 flex-wrap">
        <PrimaryButton
          onClick={openCta}
          testid={`action-open-${action.id}`}
          className="text-xs"
        >
          {action.cta_label || "Ouvrir"} <ChevronRight className="h-3 w-3 ml-0.5 inline" />
        </PrimaryButton>
        <GhostButton
          onClick={onDone}
          testid={`action-done-${action.id}`}
          className="text-xs"
        >
          <CheckCircle2 className="h-3 w-3 mr-1 inline" strokeWidth={2} /> Fait
        </GhostButton>
        {onSimulate && (
          <button
            onClick={onSimulate}
            className="text-[11px] font-mono uppercase tracking-[0.24em] hover:opacity-70 transition-opacity"
            style={{ color: CHAMPAGNE }}
            data-testid={`action-simulate-${action.id}`}
            title="Comparer 3 timings pour cette action"
          >
            <Beaker className="h-3 w-3 mr-1 inline" strokeWidth={2} /> Simuler
          </button>
        )}
        <button
          onClick={onDismiss}
          className="text-[11px] font-mono uppercase tracking-[0.24em] hover:opacity-70 transition-opacity"
          style={{ color: SLATE }}
          data-testid={`action-dismiss-${action.id}`}
        >
          <XCircle className="h-3 w-3 mr-1 inline" strokeWidth={2} /> Ignorer
        </button>
      </div>
    </motion.article>
  );
}


function MetaBlock({ icon: Icon, label, value, accent, testid }) {
  return (
    <div
      className="rounded-md px-3 py-2"
      style={{ background: `${accent}18`, border: `1px solid ${accent}33` }}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em]"
           style={{ color: SLATE }}>
        <Icon className="h-3 w-3" strokeWidth={1.8} /> {label}
      </div>
      <div className="mt-0.5 font-serif text-sm tabular-nums" style={{ color: NAVY }}>
        {value}
      </div>
    </div>
  );
}
